import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import classNames from 'classnames'
import * as R from 'ramda'
import { Defaults, PermissionArea } from '@pbt/pbt-ui-components'

import ConversationDateRangeFilter from '~/components/dashboard/communications/table/ConversationDateRangeFilter'
import NoRecords from '~/components/dashboard/landing/widgets/NoRecords'
import { getPlaceHolders } from '~/components/dashboard/landing/widgets/TableWidget'
import DialogNames from '~/constants/DialogNames'
import i18n from '~/locales/i18n'
import {
  fetchClientConversationsList,
  setConversationsListFilters,
} from '~/store/actions/conversations'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  getConversationListFetching,
  getConversationsListFilters,
  getConversationsListObjects,
  getConversationsTotalCount,
} from '~/store/reducers/conversations'
import type { Conversation } from '~/types'
import useDialog from '~/utils/useDialog'

import CommunicationRow from './CommunicationRow'
import RowSkeleton from './RowSkeleton'
import SnapshotHeader from './SnapshotHeader'

import styles from './CommunicationSnapshot.module.scss'

const columns = [
  {
    id: 'expandConversationThread',
    label: '',
  },
  {
    id: 'lastMessageDate',
    label: i18n.t('Common:LAST_MESSAGE'),
    FilterComponent: ConversationDateRangeFilter,
  },
  {
    id: 'messageSubject',
    label: '',
  },
  {
    id: 'linkedAppointment',
    label: i18n.t('Clients:COMMUNICATION_WIDGET.LINKED'),
  },
]

const CommunicationsSnapshot = ({
  clientId,
  patientId,
  isExpanded,
  onExpand,
  rowsCount,
}: {
  clientId: string
  isExpanded?: boolean
  onExpand: () => void
  patientId?: string
  rowsCount: number
}) => {
  const { t } = useTranslation(['Common', 'Clients'])
  const dispatch = useDispatch()
  const conversations = useSelector(getConversationsListObjects)
  const conversationsTotalCount = useSelector(getConversationsTotalCount)
  const filters = useSelector(getConversationsListFilters)
  const isLoadingConversations = useSelector(getConversationListFetching)
  const [isLoading, setIsLoading] = useState(false)
  const { create: communicationCreatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.COMMUNICATION),
  )
  const [openNewConversationDialog] = useDialog(
    DialogNames.NEW_CONVERSATION_WITH_STEPS,
  )
  const placeholderRows = getPlaceHolders(
    rowsCount || Defaults.INFINITE_LIST_BATCH_LOAD_COUNT,
  )

  const handleNewConversationClick = () => {
    openNewConversationDialog({ clientId, patientId })
  }

  useEffect(() => {
    const excludeSystemMessagesFilter = filters?.excludeSystemMessages ?? {
      value: true,
    }

    dispatch(
      setConversationsListFilters({
        ...filters,
        category: { value: null },
        excludeSystemMessages: excludeSystemMessagesFilter,
        patientIds: { value: patientId ? [patientId] : null },
      }),
    )
  }, [patientId])

  useEffect(() => {
    dispatch(fetchClientConversationsList(clientId, 0, rowsCount, false))
  }, [clientId, filters, rowsCount])

  useEffect(() => {
    if (isLoadingConversations) {
      setIsLoading(true)
    }
  }, [isLoadingConversations])

  useEffect(() => {
    const hasMessages = (conversation: Conversation) =>
      !R.isNil(R.prop('messages', conversation))
    const hasAllMessages = R.all(hasMessages)(conversations)
    if (conversations.length && hasAllMessages) {
      setIsLoading(false)
    } else if (isLoading && !conversations.length && !isLoadingConversations) {
      setIsLoading(false)
    }
  }, [conversations, isLoadingConversations])

  const showNoItems = conversations && R.isEmpty(conversations) && !isLoading
  const totalCount = isLoadingConversations ? 0 : conversationsTotalCount
  const displayCount = R.min(totalCount, rowsCount)

  return (
    <Grid
      className={classNames(styles.snapshot, {
        [styles.noBorder]: Boolean(isExpanded),
      })}
    >
      <SnapshotHeader
        isExpanded={isExpanded}
        name={t('Common:COMMUNICATIONS')}
        showNoItems={showNoItems}
        onExpand={onExpand}
      />
      {showNoItems ? (
        <NoRecords
          action={t('Clients:SNAPSHOTS_AND_RECORDS.ADD_CONVERSATION')}
          text={t('Clients:SNAPSHOTS_AND_RECORDS.NO_COMMUNICATIONS')}
          onClick={
            communicationCreatePermissions
              ? handleNewConversationClick
              : undefined
          }
        />
      ) : (
        <Table className={styles.table} size="small">
          <caption>
            {t('Clients:COMMUNICATION_WIDGET.THREAD_COUNTER', {
              displayCount,
              totalCount,
            })}
          </caption>
          <TableHead className={styles.tableHeader}>
            <TableRow>
              {columns.map(({ label, id }) => (
                <TableCell className={styles[id]} key={id}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingConversations ? (
              <RowSkeleton columns={columns} rows={placeholderRows} />
            ) : (
              conversations.map((item) => (
                <CommunicationRow
                  LoadingSkeleton={<RowSkeleton columns={columns} rows={[1]} />}
                  conversation={item}
                  isLoading={isLoading}
                  key={item.id}
                />
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Grid>
  )
}

export default CommunicationsSnapshot
